import './accordion.agentsync-container.scss';
import './accordion.broker-form-container.scss';

import * as React from 'react';

import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';

import AgentSyncAccordions from './accordion.agentsync-container';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import BrokerForm from './broker-form';
import { CreateBrokerFields } from './broker-tabs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LicenseList from './license-list';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { LicensePaginationSort, useGetBrokerage } from '@converge/graphql';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  backgroundColor: 'white',
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: 'auto', // Align the expand icon to the right
    transform: 'rotate(0deg)', // Initial rotation
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export interface ILicenses {
  __typename?: 'HasLicenseTypeEdge' | undefined;
  cursor?: number;
  node: {
    __typename?: 'HasLicenseType' | undefined;
    uuid?: string | null | undefined;
    licenseNumber?: string | null | undefined;
    expirationDate?: Date | string | null;
    source?: string | null | undefined;
    licenseType?: string | null | undefined;
    perpetual?: boolean | undefined | null;

    licenseHolder?:
      | {
          lastName?: string | null;
          firstName?: string | null;
          uuid?: string | null;
        }
      | null
      | undefined;

    region?:
      | {
          __typename?: 'Region' | undefined;
          name?: string | null | undefined;
          uuid?: string | null | undefined;
          iso2Code?: string | null | undefined;
        }
      | null
      | undefined;
  };
}

interface BrokerFormAccordionProps {
  setPrimary: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  formMethods: UseFormReturn<CreateBrokerFields, unknown>;
  brokerUuid: string | undefined;
  isReadOnly?: boolean;
  brokerLicense: ILicenses[];
  brokerName?: string;
  onSubmitHandler: () => SubmitHandler<CreateBrokerFields>;
  associateBrokerageUuid: string;
  primary: boolean | undefined;
  isDisabledPrimary: boolean | undefined;
  setIsUserBdm: React.Dispatch<React.SetStateAction<boolean>>;
  isUserSetBdm: boolean;
  setIsUserUnderwriter: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDisabledPrimary: React.Dispatch<React.SetStateAction<boolean>>;
  isUserUnderwriter: boolean;
  isLoading?: boolean;
  onLicenseSortChange?: (sortModel: LicensePaginationSort) => void;
}
export default function BrokerFormAccordion({
  setPrimary,
  isReadOnly,
  primary,
  brokerUuid,
  formMethods,
  brokerLicense,
  associateBrokerageUuid,
  isDisabledPrimary,
  setIsDisabledPrimary,
  brokerName,
  setIsUserBdm,
  isUserSetBdm,
  isUserUnderwriter,
  setIsUserUnderwriter,
  onSubmitHandler,
  isLoading = false,
  onLicenseSortChange,
}: BrokerFormAccordionProps) {
  // State for each individual panel
  const [isPanel01pen, setPanel01pen] = React.useState(true); // First panel open by default
  const [isPanel02pen, setPanel02pen] = React.useState(false);

  const handleSubmitFromChild = async () => {
    const isValid = await formMethods.trigger(); // Trigger form validation

    if (isValid) {
      // Instead of manually calling onSubmitHandler, use formMethods.handleSubmit to trigger form submission
      formMethods.handleSubmit(onSubmitHandler())(); // This triggers the form submission process
    }
  };

  const { brokerages } = useGetBrokerage({
    uuid: associateBrokerageUuid,
    enabled: Boolean(associateBrokerageUuid),
  });

  // Handler to toggle each panel
  const handleChangePanel1 = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPanel01pen(newExpanded);
  };

  const handleChangePanel2 = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPanel02pen(newExpanded);
  };

  return (
    <div className="accordion">
      <div>{!brokerUuid && <AgentSyncAccordions isBroker />}</div>

      <Accordion
        expanded={isPanel01pen}
        onChange={handleChangePanel1}
        className="panel"
      >
        <AccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
          expandIcon={
            <ExpandMoreIcon
              sx={{
                transform: isPanel01pen ? 'rotate(270deg)' : 'rotate(-90deg)',
              }}
              className="expand-icon"
            />
          }
        >
          <Typography className="title">Broker Information</Typography>
        </AccordionSummary>
        <AccordionDetails className="broker-info-wrapper">
          <div className="broker-form-wrapper">
            <BrokerForm
              isReadOnly={isReadOnly}
              isDisabledPrimary={isDisabledPrimary}
              setIsDisabledPrimary={setIsDisabledPrimary}
              setPrimary={setPrimary}
              brokerUuid={brokerUuid}
              primary={primary}
              formMethods={formMethods}
              setIsUserBdm={setIsUserBdm}
              isUserSetBdm={isUserSetBdm}
              setIsUserUnderwriter={setIsUserUnderwriter}
              isUserUnderwriter={isUserUnderwriter}
            />
          </div>
        </AccordionDetails>
      </Accordion>
      <div className="accordion license-accordion">
        <Accordion
          expanded={isPanel02pen && !isReadOnly}
          onChange={handleChangePanel2}
          className="panel"
        >
          <AccordionSummary
            aria-controls="panel1d-content"
            id="panel1d-header"
            expandIcon={
              <ExpandMoreIcon
                sx={{
                  transform: isPanel02pen ? 'rotate(270deg)' : 'rotate(-90deg)',
                }}
                className="expand-icon"
              />
            }
          >
            <Typography className="title">Licenses</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              <LicenseList
                brokerLicense={brokerLicense}
                brokerUuid={brokerUuid}
                handleSubmitFromChild={handleSubmitFromChild}
                associateBrokerageUuid={associateBrokerageUuid}
                brokerage={brokerages[0]}
                brokerName={brokerName}
                isLoading={isLoading}
                onSortChange={onLicenseSortChange}
              />
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}
